import { C1TableRedux, C1TableReduxColumn, C1TableReduxProps, C1TableGroupHeaderRow, C1TableVariant } from 'components/core/list/C1Table/C1TableRedux'
import { FC, useCallback, useMemo, useState } from 'react'
import { HookLike, ListHookFlags } from 'redux_toolkit/lib/createPagedListApi'
import { InvalidateHookLike } from 'redux_toolkit/lib/createUnaryApi'

export type C1TableReduxHookProps<ReqT, T, ExtraProps> = {
    cols: C1TableReduxColumn<T, ExtraProps>[]
    request: ReqT
    hook: HookLike<ReqT, T>
    invalidateHook: InvalidateHookLike
    hookFlags?: ListHookFlags
    groupBy?: (item: T) => string
    GroupLabel?: FC<{ groupKey: string; rows: T[] }>
    // 'default' shows full table, 'minimal' limits to 5 results with no pagination/borders
    variant?: C1TableVariant
} & Pick<C1TableReduxProps<T, ExtraProps>, 'emptyState' | 'staticExtraProps' | 'getId' | 'extraFooterActions' | 'setSelections' | 'selections' | 'onRowClick' | 'ExpandedContent' | 'totalCount'>

export const C1TableReduxHook = <ReqT, T, ExtraProps = object>({
    cols,
    request,
    hook: useHook,
    invalidateHook: useInvalidateHook,
    hookFlags,
    groupBy,
    GroupLabel,
    emptyState,
    staticExtraProps,
    getId,
    extraFooterActions,
    setSelections,
    selections,
    onRowClick,
    ExpandedContent,
    totalCount,
    variant = 'default',
}: C1TableReduxHookProps<ReqT, T, ExtraProps>): React.ReactElement => {
    const { pageSize: hookPageSize, notify, ...rest } = { ...hookFlags }
    const pageSize = hookPageSize
    const [page, setPage] = useState<number>(0)

    const { data, isError, isLoading, isBackgroundLoading, error, canLoadMore, shouldInvalidateList, getPage } = useHook(request, {
        pageSize,
        notify,
        ...rest,
    })
    const invalidateList = useInvalidateHook()

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const groupedData = useMemo(() => {
        if (!groupBy || !GroupLabel || !data) return undefined

        return data.map((pageData) => {
            const groups = new Map<string, T[]>()

            pageData.forEach((item) => {
                const groupKey = groupBy(item)
                let group = groups.get(groupKey)
                if (!group) {
                    group = []
                    groups.set(groupKey, group)
                }
                group.push(item)
            })

            return Array.from(groups.entries()).map(([groupKey, items]): C1TableGroupHeaderRow<T> => ({
                id: groupKey,
                groupKey,
                groupLabel: <GroupLabel groupKey={groupKey} rows={items} />,
                rows: items,
            }))
        })
    }, [data, groupBy, GroupLabel])

    return <C1TableRedux<T, ExtraProps>
        data={data}
        groupedData={groupedData}
        isError={isError}
        isLoading={isLoading}
        isBackgroundLoading={isBackgroundLoading}
        error={error}
        canLoadMore={canLoadMore}
        cols={cols}
        onPageChange={onPageChange}
        emptyState={emptyState}
        pageSize={pageSize}
        page={page}
        shouldReload={notify ? shouldInvalidateList : undefined}
        onReload={notify ? invalidateList : undefined}
        staticExtraProps={staticExtraProps}
        getId={getId}
        extraFooterActions={extraFooterActions}
        setSelections={setSelections}
        selections={selections}
        onRowClick={onRowClick}
        ExpandedContent={ExpandedContent}
        totalCount={totalCount}
        variant={variant}
    />
}
